import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { usePortal } from "@/features/portal/portal-context";

/**
 * Persistent "you're viewing someone else's brand" strip shown above the editor
 * top bar. Only rendered when a super admin has opened a brand they don't belong
 * to (see {@link usePortal}'s `viewingOthersBrand`); for your own brands it
 * renders nothing. The amber treatment matches the portal's draft / attention
 * accent so it reads as "caution," not "error."
 */
export function AdminViewBanner() {
  const { viewingOthersBrand, activeBrand } = usePortal();
  const navigate = useNavigate();

  if (!viewingOthersBrand || !activeBrand) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-3 border-b px-9 py-2.5"
      style={{
        background:
          "linear-gradient(95deg, rgba(245,185,69,.18), rgba(224,55,154,.12))",
        borderColor: "color-mix(in oklab, #f5b945 40%, var(--border))",
      }}
    >
      <div className="grid size-[30px] shrink-0 place-items-center rounded-[9px] bg-[rgba(245,185,69,.16)] text-[#b9851f] dark:text-[#f5b945]">
        <ShieldCheck className="size-[17px]" />
      </div>
      <p className="min-w-0 flex-1 text-[12.5px] leading-snug text-muted-foreground">
        <b className="font-bold text-foreground">Admin view.</b> You’re editing{" "}
        <b className="font-bold text-foreground">{activeBrand.name}</b>
        {activeBrand.ownerName ? (
          <>
            , owned by{" "}
            <b className="font-bold text-foreground">{activeBrand.ownerName}</b>
          </>
        ) : null}{" "}
        — not one of your own brands. Changes save against {activeBrand.name} and
        are attributed to you.
      </p>
      <button
        type="button"
        onClick={() => navigate("/admin/brands")}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-[9px] border bg-card px-3 py-[7px] text-[12px] font-bold text-[#b9851f] transition-colors hover:brightness-105 dark:text-[#f5b945]"
        style={{ borderColor: "color-mix(in oklab, #f5b945 45%, transparent)" }}
      >
        <ArrowLeft className="size-[14px]" />
        Back to All brands
      </button>
    </div>
  );
}
